<template>
  <canvas class="chart__canvas" ref="chart" data-cy="canvas"></canvas>
</template>

<script setup lang="ts">
import { useDate } from '@/composables/useDate.ts'
import { useAccountStore } from '@/stores/useAccountStore'
import { useCubeStore } from '@/stores/useCubeStore'
import { useChart } from '@/composables/useChart.js'
import 'chartjs-adapter-date-fns'
import { de } from 'date-fns/locale'
import { useUnits } from '@/composables/useUnits.ts'
import { useColors } from '@/composables/useColors.ts'
import { computed, useTemplateRef, watch } from 'vue'
import type { HistoryDataState } from '@/types/HistoryData';
import { storeToRefs } from 'pinia'
import type { ChartConfiguration } from 'chart.js'

const { moment } = useDate()

const chartRef = useTemplateRef('chart')
const { formatKWH, formatPercent } = useUnits()
const { supplier, showInverterData } = storeToRefs(useCubeStore())
const { updateMonthChart } = useChart(chartRef)

const {
  colorPrimary,
  colorSecondary,
  colorSecondaryHighContrast
} = useColors()

const { isHighContrast } = storeToRefs(useAccountStore())

const colorSupplier = computed(() => {
  if (isHighContrast.value) {
    return colorSecondaryHighContrast.value
  }
  return colorSecondary.value
})

const model = defineModel<HistoryDataState>();

const chartConfig = computed(() => {
  const config: ChartConfiguration = {
    type: 'bar',
      options: {
        maintainAspectRatio: false,
        // fontColor: '#dddddd',
        legend: {
        position: 'bottom',
      },
      scales: {
        xAxes: [
          {
            // categorySpacing: 1,
            stacked: true,
            gridLines: {
              color: 'rgba(0, 0, 0, 0.03)',
              zeroLineColor: 'transparent',
            },
            display: true,
            ticks: {
              autoSkip: true,
              autoSkipPadding: 5,
            },
            type: 'time',
            offset: true,
            time: {
              unit: 'day',
              displayFormats: {
                day: 'E dd.MM',
              },
            },
            adapters: {
              date: {
                locale: de
              }
            },
          },
        ],
          yAxes: [
          {
            id: 'yAbove',
            stacked: true,
            ticks: {
              callback: (value: number) => {
                return formatKWH(Math.abs(value))
              },
            },
            gridLines: {
              color: 'rgba(0, 0, 0, 0.03)',
              zeroLineWidth: 1,
              zeroLineColor: '#2C292E',
            },
          },
          {
            id: 'yNonStacked',
            display: false,
            stacked: false,
            ticks: {
              callback: (value: number) => {
                return formatKWH(Math.abs(value))
              },
              suggestedMax: 1,
              suggestedMin: 0,
              beginAtZero: true,
            },
            gridLines: {
              color: 'rgba(0, 0, 0, 0.03)',
              zeroLineColor: 'transparent',
            },
          },
          {
            id: 'yPercent',
            position: 'right',
            stacked: false,
            display: false,
            ticks: {
              callback: (value) => {
                return formatPercent(value)
              },
              suggestedMax: 100,
              suggestedMin: 0,
              beginAtZero: true,
            },
            gridLines: {
              drawOnChartArea: false
            },
            type: 'linear',
          },
        ],
      },
      tooltips: {
        mode: 'index',
          callbacks: {
          title: (item, data: any) => {
            return moment(data.labels[item[0].index]).format(
              'DD.MM.YYYY'
            )
          },
            label: (item, data) => {
            const dataset = data.datasets[item.datasetIndex]
            const value = (item.yLabel + '').replace(/^-/, '')
            let label = dataset.label
            return label + ': ' + formatKWH(value)
          },
        },
      },
    },
    data: {
      labels: [],
        datasets: [
        {
          label: 'Vom Marktplatz',
          fill: 'origin',
          lineTension: 0.8,
          barPercentage: 0.8,
          categoryPercentage: 1,
          cubicInterpolationMode: 'monotone',
          backgroundColor: colorPrimary.value,
          data: [],
          borderJoinStyle: 'round',
          borderColor: 'transparent',
          pointStyle: 'line',
          yAxisID: 'yAbove',
        },
        {
          label: 'Von ' + supplier.value.name,
          fill: 'origin',
          backgroundColor: colorSupplier.value,
          data: [],
          lineTension: 0.8,
          barPercentage: 0.8,
          categoryPercentage: 1,
          cubicInterpolationMode: 'monotone',
          borderJoinStyle: 'round',
          borderColor: 'transparent',
          pointStyle: 'line',
          yAxisID: 'yAbove',
        },
        {
          label: 'An Marktplatz',
          fill: 'origin',
          lineTension: 0.8,
          barPercentage: 0.8,
          categoryPercentage: 1,
          cubicInterpolationMode: 'monotone',
          backgroundColor: colorPrimary.value,
          data: [],
          borderJoinStyle: 'round',
          borderColor: 'transparent',
          pointStyle: 'line',
          yAxisID: 'yAbove',
        },
        {
          label: 'An ' + supplier.value.name,
          fill: 'origin',
          backgroundColor: colorSupplier.value,
          data: [],
          lineTension: 0.8,
          barPercentage: 0.8,
          categoryPercentage: 1,
          cubicInterpolationMode: 'monotone',
          borderJoinStyle: 'round',
          borderColor: 'transparent',
          pointStyle: 'line',
          yAxisID: 'yAbove',
        },
        {
          type: 'line',
          label: 'Einspeisung ins Netz',
          fill: false,
          backgroundColor: '#beb',
          borderWidth: 2,
          borderColor: '#bfb',
          data: [],
          lineTension: 0.8,
          barPercentage: 0.8,
          categoryPercentage: 1,
          cubicInterpolationMode: 'monotone',
          borderJoinStyle: 'round',
          yAxisID: 'yNonStacked',
          pointRadius: 0,
        },
        {
          type: 'line',
          label: 'Aus dem Netz',
          fill: false,
          backgroundColor: '#ecc',
          borderWidth: 2,
          borderColor: '#fbb',
          data: [],
          lineTension: 0.8,
          barPercentage: 0.8,
          categoryPercentage: 1,
          cubicInterpolationMode: 'monotone',
          borderJoinStyle: 'round',
          yAxisID: 'yNonStacked',
          pointRadius: 0,
        },
      ],
    },
  }

  // if (showInverterData.value) {
  //   config.data.datasets.push({
  //     type: 'line',
  //     label: 'Wechselrichter Produktionsleistung',
  //     fill: false,
  //     data: [],
  //     borderColor: 'rgba(109, 130, 96, 0.8)',
  //     backgroundColor: "rgba(109, 130, 96, 0.8)",
  //     borderWidth: 2,
  //     lineTension: 0.8,
  //     barPercentage: 0.8,
  //     categoryPercentage: 1,
  //     cubicInterpolationMode: 'monotone',
  //     borderJoinStyle: 'round',
  //     yAxisID: 'yNonStacked',
  //     pointRadius: 0,
  //   })
  // }

  // TODO DayChart.vue shows projection data if store.getters.isMeterless.
  // Why not show this in the MonthChart too?

  return config
})

watch(model, (data: HistoryDataState) => {
  updateMonthChart(model.value, chartConfig.value)
});
</script>
